import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup, sendEmailVerification, signOut } from 'firebase/auth';
import { auth, googleProvider, db } from '../firebase';
import { Loader2, Mail, Lock, AlertCircle, ArrowRight, ShieldAlert, RefreshCw } from 'lucide-react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import PasswordInput from '../components/auth/PasswordInput';
import { useToast } from '../context/ToastContext';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [unverifiedUser, setUnverifiedUser] = useState(null); // holds the unverified firebase user temporarily
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const startCooldown = () => {
    setResendCooldown(60);
    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || !unverifiedUser) return;
    try {
      await sendEmailVerification(unverifiedUser);
      startCooldown();
      addToast('تم إرسال رابط التفعيل إلى بريدك بنجاح!', 'success');
    } catch (err) {
      console.error('Resend error:', err);
      addToast('حدث خطأ أثناء إعادة إرسال الرابط.', 'error');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setUnverifiedUser(null);
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // ── Email Verification Gate ──
      if (!user.emailVerified) {
        setUnverifiedUser(user);
        await signOut(auth); // Kill session immediately
        setLoading(false);
        addToast('يرجى التحقق من بريدك الإلكتروني أولاً!', 'warning');
        return;
      }
      
      addToast('تم تسجيل الدخول بنجاح!', 'success');

      // Check user role in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // --- CUSTOMERS BACKFILL (if missing) ---
        const custQ = query(collection(db, 'customers'), where('userId', '==', user.uid));
        const custSnap = await getDocs(custQ);
        if (custSnap.empty) {
          const uName = userData.name || userData.displayName || user.displayName || 'مستخدم عائد';
          await addDoc(collection(db, 'customers'), {
            name: uName,
            username: userData.username || 'user_' + crypto.randomUUID().split('-')[0],
            email: user.email,
            whatsapp: userData.phone || '',
            level: 'beginner',
            status: userData.role === 'instructor' ? 'instructor' : 'interested',
            amountPaid: 0,
            source: 'login_backfill',
            userId: user.uid,
            createdAt: serverTimestamp(),
          });
        }
        // ---------------------------------------

        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl, { replace: true });
        } else if (userData.role === 'admin') {
          navigate('/admin/overview');
        } else if (userData.role === 'instructor') {
          navigate('/instructor-dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl, { replace: true });
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('البريد الإلكتروني أو كلمة المرور غير صحيحة');
        addToast('البريد الإلكتروني أو كلمة المرور غير صحيحة', 'error');
      } else {
        setError('حدث خطأ أثناء تسجيل الدخول. يرجى المحاولة مرة أخرى.');
        addToast('حدث خطأ أثناء تسجيل الدخول. يرجى المحاولة مرة أخرى.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setUnverifiedUser(null);
    setGoogleLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        const generatedUsername = 'user_' + crypto.randomUUID().split('-')[0];
        
        await setDoc(userDocRef, {
          uid: user.uid,
          name: user.displayName || 'مستخدم جديد',
          username: generatedUsername,
          email: user.email,
          photoURL: user.photoURL || '',
          role: 'student',
          registrationDate: serverTimestamp(),
          lastLogin: serverTimestamp()
        });

        await addDoc(collection(db, 'customers'), {
          name: user.displayName || 'مستخدم جديد',
          username: generatedUsername,
          email: user.email,
          whatsapp: '',
          level: 'beginner',
          status: 'interested',
          amountPaid: 0,
          source: 'google_login',
          userId: user.uid,
          createdAt: serverTimestamp(),
        });

        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl, { replace: true });
        } else {
          navigate('/dashboard');
        }
      } else {
        const existingData = userDoc.data();
        const updates = { lastLogin: serverTimestamp() };
        
        // Backfill missing fields that prevent users from appearing in AdminUsers
        if (!existingData.registrationDate) {
          updates.registrationDate = existingData.createdAt || serverTimestamp();
        }
        if (!existingData.name && existingData.displayName) {
          updates.name = existingData.displayName;
        } else if (!existingData.name) {
          updates.name = 'مستخدم بدون اسم';
        }

        await updateDoc(userDocRef, updates);

        const userData = { ...existingData, ...updates };

        // --- CUSTOMERS BACKFILL FOR EXISTING GOOGLE USERS ---
        const custQ = query(collection(db, 'customers'), where('userId', '==', user.uid));
        const custSnap = await getDocs(custQ);
        if (custSnap.empty) {
          const uName = userData.name || userData.displayName || user.displayName || 'مستخدم عائد';
          await addDoc(collection(db, 'customers'), {
            name: uName,
            username: userData.username || 'user_' + crypto.randomUUID().split('-')[0],
            email: user.email,
            whatsapp: userData.phone || '',
            level: 'beginner',
            status: userData.role === 'instructor' ? 'instructor' : 'interested',
            amountPaid: 0,
            source: 'google_backfill',
            userId: user.uid,
            createdAt: serverTimestamp(),
          });
        }
        // ----------------------------------------------------

        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl, { replace: true });
        } else if (userData.role === 'admin') {
          navigate('/admin/overview');
        } else if (userData.role === 'instructor') {
          navigate('/instructor-dashboard');
        } else {
          navigate('/dashboard');
        }
      }
      
      addToast('تم تسجيل الدخول باستخدام Google بنجاح', 'success');
    } catch (err) {
      console.error(err);
      setError('فشل الدخول باستخدام حساب جوجل، يرجى المحاولة مرة أخرى.');
      addToast('فشل الدخول باستخدام حساب جوجل.', 'error');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#050505] relative overflow-hidden px-4 transition-colors duration-700">
      {/* Ambient Cinematic Glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-500/20 dark:bg-primary-600/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-500/10 dark:bg-purple-600/10 rounded-full blur-[150px] translate-y-1/3 -translate-x-1/4 pointer-events-none"></div>

      <div className="relative z-10 max-w-md w-full bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl rounded-lg shadow-2xl shadow-slate-200/50 dark:shadow-black/80 border border-slate-100 dark:border-white/10 p-8 md:p-10 transform transition-all duration-500 hover:shadow-primary-500/10">
        <div className="text-center mb-10">
          <Link to="/" className="inline-block text-4xl font-black bg-clip-text text-transparent bg-gradient-to-l from-primary-600 to-primary-400 dark:from-primary-400 dark:to-purple-400 mb-3 drop-shadow-sm">
            لُبـــــــــاب
          </Link>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">تسجيـــــل الدخـــــــول</h2>
          <p className="text-slate-500 dark:text-zinc-400 mt-2 font-bold">مرحباً بك في الاستوديو التعليمي</p>
        </div>

        {/* ── Unverified Email Warning ── */}
        {unverifiedUser && (
          <div className="mb-6 bg-amber-50/80 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-amber-700 dark:text-amber-400 p-5 rounded-2xl backdrop-blur-sm animate-in fade-in zoom-in-95 duration-300">
            <div className="flex items-start gap-3 mb-4">
              <ShieldAlert className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-black text-lg mb-1">البريد الإلكتروني غير مُفعّل</p>
                <p className="text-md font-bold opacity-80">يرجى تفعيل بريدك الإلكتروني للوصول لحسابك. تحقق من صندوق الوارد والبريد المزعج.</p>
              </div>
            </div>
            <button
              onClick={handleResendVerification}
              disabled={resendCooldown > 0}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${resendCooldown > 0 ? '' : 'group-hover:rotate-180 transition-transform'}`} />
              {resendCooldown > 0 ? `إعادة الإرسال بعد ${resendCooldown} ثانية` : 'إعادة إرسال رابط التفعيل'}
            </button>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-rose-50/80 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 p-4 rounded-2xl flex items-start gap-3 backdrop-blur-sm animate-in fade-in zoom-in-95 duration-300">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-md font-bold leading-relaxed">{error}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6" dir="rtl">
          <div>
            <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">البريد الإلكتروني</label>
            <div className="relative group">
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none transition-colors group-focus-within:text-primary-500">
                <Mail className="h-5 w-5 text-slate-400 dark:text-zinc-500 transition-colors group-focus-within:text-primary-500" />
              </div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block text-lg w-full pr-12 pl-4 py-3.5 border border-slate-200 dark:border-white/10 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white/50 dark:bg-black/20 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 transition-all duration-300 hover:bg-white dark:hover:bg-white/5 font-medium"
                placeholder="أدخل بريدك الإلكتروني"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-md font-bold text-slate-700 dark:text-zinc-300">كلمة المرور</label>
              <Link to="/forgot-password" className="text-md font-bold text-primary-600 dark:text-primary-400 hover:text-primary-500 transition-colors">
                نسيت كلمة المرور؟
              </Link>
            </div>
            <div className="relative group">
              <div className="absolute top-1/2 -translate-y-1/2 right-4 flex items-center pointer-events-none transition-colors group-focus-within:text-primary-500 z-10">
                <Lock className="h-5 w-5 text-slate-400 dark:text-zinc-500 transition-colors group-focus-within:text-primary-500" />
              </div>
              <PasswordInput
                id="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                showValidator={false}
                className="pl-4 pr-12 text-lg py-3.5 border border-slate-200 dark:border-white/10 rounded-2xl bg-white/50 dark:bg-black/20"
                placeholder="أدخل كلمة المرور"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || googleLoading}
            className="w-full flex text-xl font-semibold justify-center items-center py-4 px-4 border border-transparent rounded-2xl text-lg font-bold text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 focus:outline-none transition-all duration-300 hover:shadow-[0_0_25px_rgba(245,158,11,0.4)] hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : 'دخـــــول للاستوديـــــو'}
          </button>
        </form>

        <div className="mt-8 flex items-center justify-between">
          <hr className="w-full border-slate-200 dark:border-white/10" />
          <span className="px-4 text-slate-400 dark:text-zinc-500 text-lg font-medium">أو</span>
          <hr className="w-full border-slate-200 dark:border-white/10" />
        </div>

        <button
          onClick={handleGoogleSignIn}
          disabled={loading || googleLoading}
          className="mt-8 w-full flex items-center justify-center gap-3 py-4 px-4 rounded-2xl border border-slate-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 backdrop-blur-md shadow-sm transition-all duration-300 hover:shadow-lg dark:hover:shadow-[0_0_20px_rgba(255,255,255,0.05)] text-slate-700 dark:text-white font-bold hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed group relative overflow-hidden"
        >
          {/* Subtle gradient hover effect for Google button */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-red-500/10 to-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          {googleLoading ? (
            <Loader2 className="w-6 h-6 animate-spin relative z-10" />
          ) : (
            <>
              <svg className="w-6 h-6 relative z-10" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span className="relative z-10">الدخول بحساب Google</span>
            </>
          )}
        </button>

        <div className="mt-8 pt-6 border-t border-slate-200/50 dark:border-white/5 text-center space-y-5">
          <p className="text-lg font-semibold text-slate-600 dark:text-zinc-400">
            ليس لديك حساب؟{' '}
            <Link to="/signup" className="font-extrabold text-xl text-primary-600 dark:text-primary-400 hover:text-primary-500 transition-colors">
              سجل الآن
            </Link>
          </p>

          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-slate-500 dark:text-zinc-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors group">
            <ArrowRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            العودة إلى الرئيسية
          </Link>
        </div>
      </div>
    </div>
  );
}
